import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatDateTime, formatTimeAgo } from '../utils/formatters';

const CATEGORY_SUGGESTIONS = [
  'Shipping - Mailers',
  'Shipping - Boxes',
  'Shipping - Tape & Labels',
  'Card Supplies - One Touches',
  'Card Supplies - Stickers',
  'Card Supplies - Rip Bags',
  'Card Supplies - Sleeves & Toploaders',
  'Packaging',
  'Other'
];
const LOCATION_SUGGESTIONS = ['Storage', 'Fulfillment', 'Casing'];

const REASONS = [
  { value: 'purchase', label: 'Purchase' },
  { value: 'used', label: 'Used' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'correction', label: 'Correction' },
  { value: 'other', label: 'Other' },
];

export default function Inventory() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [reorderCount, setReorderCount] = useState(0);
  const [filters, setFilters] = useState({ search: '', category: '', location: '', low_stock: false, needs_reorder: false });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', category: '', location: '', unit: 'units', current_stock: 0, min_stock: 0, notes: '', needs_reorder: false });
  const [adjustForm, setAdjustForm] = useState({ qty: '', reason: 'used', notes: '' });
  const [showCatDropdown, setShowCatDropdown] = useState(false);
  const [showLocDropdown, setShowLocDropdown] = useState(false);
  const [startingCheckin, setStartingCheckin] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.category) params.category = filters.category;
      if (filters.location) params.location = filters.location;
      if (filters.low_stock) params.low_stock = '1';
      if (filters.needs_reorder) params.needs_reorder = '1';
      const { data } = await api.get('/api/inventory', { params });
      setItems(data.items);
      setCategories(data.categories);
      setLocations(data.locations || []);
      setReorderCount(data.reorderCount || 0);
    } catch (err) { console.error('Inventory fetch error:', err); }
  }, [filters]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  async function fetchDetail(id) {
    try {
      const { data } = await api.get(`/api/inventory/${id}`);
      setDetail(data);
      setSelected(data.item);
    } catch (err) { console.error('Detail fetch error:', err); }
  }

  async function handleCreate(e) {
    e.preventDefault();
    try {
      await api.post('/api/inventory', {
        ...form,
        current_stock: parseInt(form.current_stock) || 0,
        min_stock: parseInt(form.min_stock) || 0
      });
      setShowForm(false);
      resetForm();
      fetchItems();
    } catch (err) { alert(err.response?.data?.error || 'Failed to create item'); }
  }

  async function handleEdit(e) {
    e.preventDefault();
    try {
      await api.put(`/api/inventory/${editItem.id}`, {
        name: form.name,
        category: form.category,
        location: form.location,
        unit: form.unit,
        min_stock: parseInt(form.min_stock) || 0,
        notes: form.notes,
        needs_reorder: form.needs_reorder,
      });
      setEditItem(null);
      resetForm();
      fetchItems();
      if (selected?.id === editItem.id) fetchDetail(editItem.id);
    } catch (err) { alert(err.response?.data?.error || 'Failed to update item'); }
  }

  async function startCheckin() {
    setStartingCheckin(true);
    try {
      const { data } = await api.post('/api/inventory/checkins');
      navigate(`/inventory/checkin/${data.id}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to start check-in');
    } finally {
      setStartingCheckin(false);
    }
  }

  async function clearReorderManual(id) {
    try {
      await api.post(`/api/inventory/${id}/clear-reorder`);
      fetchItems();
      if (selected?.id === id) fetchDetail(id);
    } catch (err) { alert(err.response?.data?.error || 'Failed to clear reorder flag'); }
  }

  async function handleAdjust(direction) {
    const qty = parseInt(adjustForm.qty);
    if (!qty || qty <= 0) return alert('Enter a positive quantity');
    const change = direction === 'remove' ? -qty : qty;
    try {
      await api.post(`/api/inventory/${selected.id}/adjust`, {
        change_qty: change,
        reason: adjustForm.reason,
        notes: adjustForm.notes
      });
      setAdjustForm({ qty: '', reason: 'used', notes: '' });
      fetchDetail(selected.id);
      fetchItems();
    } catch (err) { alert(err.response?.data?.error || 'Failed to adjust stock'); }
  }

  async function handleDelete(item) {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/inventory/${item.id}`);
      setSelected(null);
      setDetail(null);
      fetchItems();
    } catch (err) { alert(err.response?.data?.error || 'Failed to delete item'); }
  }

  function resetForm() {
    setForm({ name: '', category: '', location: '', unit: 'units', current_stock: 0, min_stock: 0, notes: '', needs_reorder: false });
  }

  function openEdit(item) {
    setForm({
      name: item.name,
      category: item.category || '',
      location: item.location || '',
      unit: item.unit || 'units',
      current_stock: item.current_stock,
      min_stock: item.min_stock,
      notes: item.notes || '',
      needs_reorder: !!item.needs_reorder,
    });
    setEditItem(item);
  }

  const isLowStock = (item) => item.min_stock > 0 && item.current_stock <= item.min_stock;
  const isStale = (item) => {
    if (!item.last_counted_at) return true;
    const days = (Date.now() - new Date(item.last_counted_at).getTime()) / (1000 * 60 * 60 * 24);
    return days >= 14;
  };

  // Merge DB categories with suggestions for the dropdown
  const allCategories = [...new Set([...CATEGORY_SUGGESTIONS, ...categories])].sort();
  const filteredCatSuggestions = allCategories.filter(c =>
    !form.category || c.toLowerCase().includes(form.category.toLowerCase())
  );
  const allLocations = [...new Set([...LOCATION_SUGGESTIONS, ...locations])].sort();
  const filteredLocSuggestions = allLocations.filter(l =>
    !form.location || l.toLowerCase().includes(form.location.toLowerCase())
  );

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Inventory</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={startCheckin} disabled={startingCheckin}>
            {startingCheckin ? 'Starting…' : 'Start Check-in'}
          </button>
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowForm(true); }}>Add Item</button>
        </div>
      </div>

      {reorderCount > 0 && (
        <div style={{ padding: '10px 14px', background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: 8, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#9b2c2c', fontSize: 14 }}>
            <strong>{reorderCount}</strong> item{reorderCount === 1 ? '' : 's'} need{reorderCount === 1 ? 's' : ''} reordering.
          </span>
          <Link to="/inventory/reorder" className="btn btn-danger btn-sm">View reorder list →</Link>
        </div>
      )}

      <div className="filter-bar">
        <input
          type="text"
          placeholder="Search items..."
          value={filters.search}
          onChange={e => setFilters({ ...filters, search: e.target.value })}
          style={{ maxWidth: '250px' }}
        />
        <select value={filters.category} onChange={e => setFilters({ ...filters, category: e.target.value })}>
          <option value="">All Categories</option>
          {[...new Set([...categories, ...CATEGORY_SUGGESTIONS])].sort().map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select value={filters.location} onChange={e => setFilters({ ...filters, location: e.target.value })}>
          <option value="">All Locations</option>
          {[...new Set([...locations, ...LOCATION_SUGGESTIONS])].sort().map(l => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
          <input type="checkbox" checked={filters.low_stock}
            onChange={e => setFilters({ ...filters, low_stock: e.target.checked })}
            style={{ width: 'auto' }} />
          Low Stock Only
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
          <input type="checkbox" checked={filters.needs_reorder}
            onChange={e => setFilters({ ...filters, needs_reorder: e.target.checked })}
            style={{ width: 'auto' }} />
          Reorder Flagged
        </label>
      </div>

      <div className="card">
        <div className="table-container sticky-col">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Location</th>
                <th style={{ textAlign: 'right' }}>Stock</th>
                <th style={{ textAlign: 'right' }}>Min Stock</th>
                <th>Unit</th>
                <th>Last Counted</th>
                <th>Flags</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} className="clickable-row" onClick={() => { fetchDetail(item.id); setAdjustForm({ qty: '', reason: 'used', notes: '' }); }}>
                  <td><strong>{item.name}</strong></td>
                  <td><span className="badge badge-default">{item.category || 'Uncategorized'}</span></td>
                  <td>{item.location ? <span className="badge badge-info">{item.location}</span> : <span style={{ color: 'var(--text-light)', fontSize: 12 }}>—</span>}</td>
                  <td style={{ textAlign: 'right', color: isLowStock(item) ? 'var(--danger)' : undefined, fontWeight: isLowStock(item) ? '700' : undefined }}>
                    {item.current_stock}
                    {isLowStock(item) && <span style={{ marginLeft: '6px', fontSize: '11px' }}>LOW</span>}
                  </td>
                  <td style={{ textAlign: 'right', color: 'var(--text-light)' }}>{item.min_stock}</td>
                  <td style={{ color: 'var(--text-light)' }}>{item.unit}</td>
                  <td style={{ fontSize: 12, color: isStale(item) ? '#d97706' : 'var(--text-light)' }}>
                    {item.last_counted_at
                      ? <span title={item.last_counted_by_name ? `by ${item.last_counted_by_name}` : ''}>{formatTimeAgo(item.last_counted_at)}</span>
                      : <em>never</em>}
                  </td>
                  <td>
                    {item.needs_reorder ? <span className="badge" style={{ background: '#fed7d7', color: '#9b2c2c' }}>Reorder</span> : null}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-light)', padding: '24px' }}>
                  No inventory items found
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit Modal */}
      {(showForm || editItem) && (
        <div className="modal-overlay" onClick={() => { setShowForm(false); setEditItem(null); }}>
          <div className="detail-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="detail-panel-header">
              <h3>{editItem ? 'Edit Item' : 'New Item'}</h3>
              <button className="detail-panel-close" onClick={() => { setShowForm(false); setEditItem(null); }}>&times;</button>
            </div>
            <form onSubmit={editItem ? handleEdit : handleCreate}>
              <div className="form-group">
                <label>Name</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Category</label>
                <input
                  value={form.category}
                  onChange={e => { setForm({ ...form, category: e.target.value }); setShowCatDropdown(true); }}
                  onFocus={() => setShowCatDropdown(true)}
                  placeholder="Type or select..."
                />
                {showCatDropdown && filteredCatSuggestions.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid var(--border)', borderRadius: '6px', boxShadow: 'var(--shadow-lg)', maxHeight: '180px', overflowY: 'auto', zIndex: 50 }}>
                    {filteredCatSuggestions.map(c => (
                      <div key={c} onClick={() => { setForm({ ...form, category: c }); setShowCatDropdown(false); }}
                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}>
                        {c}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Location</label>
                <input
                  value={form.location}
                  onChange={e => { setForm({ ...form, location: e.target.value }); setShowLocDropdown(true); }}
                  onFocus={() => setShowLocDropdown(true)}
                  onBlur={() => setTimeout(() => setShowLocDropdown(false), 150)}
                  placeholder="Storage / Fulfillment / Casing / …"
                />
                {showLocDropdown && filteredLocSuggestions.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid var(--border)', borderRadius: '6px', boxShadow: 'var(--shadow-lg)', maxHeight: '180px', overflowY: 'auto', zIndex: 50 }}>
                    {filteredLocSuggestions.map(l => (
                      <div key={l} onClick={() => { setForm({ ...form, location: l }); setShowLocDropdown(false); }}
                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}>
                        {l}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Unit</label>
                  <input value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} placeholder="e.g. rolls, packs" />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Min Stock (alert threshold)</label>
                  <input type="number" value={form.min_stock} onChange={e => setForm({ ...form, min_stock: e.target.value })} min={0} />
                </div>
              </div>
              {!editItem && (
                <div className="form-group">
                  <label>Initial Stock</label>
                  <input type="number" value={form.current_stock} onChange={e => setForm({ ...form, current_stock: e.target.value })} min={0} />
                </div>
              )}
              {editItem && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.needs_reorder}
                      onChange={e => setForm({ ...form, needs_reorder: e.target.checked })}
                      style={{ width: 'auto' }} />
                    Flag for reorder
                  </label>
                </div>
              )}
              <div className="form-group">
                <label>Notes</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="submit" className="btn btn-primary">{editItem ? 'Save' : 'Create'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowForm(false); setEditItem(null); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selected && detail && !editItem && (
        <div className="modal-overlay" onClick={() => { setSelected(null); setDetail(null); }}>
          <div className="detail-panel" onClick={e => e.stopPropagation()}>
            <div className="detail-panel-header">
              <h3>{selected.name}</h3>
              <button className="detail-panel-close" onClick={() => { setSelected(null); setDetail(null); }}>&times;</button>
            </div>

            <div className="auto-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              <div><label>Category</label><div><span className="badge badge-default">{selected.category || 'Uncategorized'}</span></div></div>
              <div><label>Location</label><div>{selected.location ? <span className="badge badge-info">{selected.location}</span> : <span style={{ color: 'var(--text-light)' }}>—</span>}</div></div>
              <div><label>Current Stock</label>
                <div style={{ fontSize: '24px', fontWeight: '700', color: isLowStock(selected) ? 'var(--danger)' : 'var(--text)' }}>
                  {selected.current_stock}
                  {isLowStock(selected) && <span style={{ fontSize: '12px', marginLeft: '8px', fontWeight: '600' }}>LOW STOCK</span>}
                </div>
              </div>
              <div><label>Min Stock</label><div>{selected.min_stock} {selected.unit}</div></div>
              <div><label>Last Counted</label>
                <div style={{ fontSize: 13 }}>
                  {selected.last_counted_at ? (
                    <>{formatTimeAgo(selected.last_counted_at)}{selected.last_counted_by_name ? ` by ${selected.last_counted_by_name}` : ''}</>
                  ) : <em style={{ color: 'var(--text-light)' }}>never</em>}
                </div>
              </div>
              <div><label>30-day Burn Rate</label>
                <div style={{ fontSize: 13 }}>
                  {detail.burn_per_week > 0 ? `${detail.burn_per_week} ${selected.unit}/week` : <span style={{ color: 'var(--text-light)' }}>—</span>}
                </div>
              </div>
            </div>
            {selected.needs_reorder ? (
              <div style={{ padding: '8px 12px', background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: 6, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#9b2c2c', fontSize: 13, fontWeight: 600 }}>Flagged for reorder</span>
                <button className="btn btn-sm btn-secondary" onClick={() => clearReorderManual(selected.id)}>Mark Restocked</button>
              </div>
            ) : null}
            {selected.notes && <p style={{ marginBottom: '16px', color: 'var(--text-secondary)', fontSize: '13px' }}>{selected.notes}</p>}

            {/* Quick Adjust */}
            <div style={{ padding: '16px', background: 'var(--bg)', borderRadius: '8px', marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '12px', fontSize: '14px' }}>Adjust Stock</h4>
              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Quantity</label>
                  <input type="number" value={adjustForm.qty} onChange={e => setAdjustForm({ ...adjustForm, qty: e.target.value })} min={1} placeholder="0" />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Reason</label>
                  <select value={adjustForm.reason} onChange={e => setAdjustForm({ ...adjustForm, reason: e.target.value })}>
                    {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Notes (optional)</label>
                <input value={adjustForm.notes} onChange={e => setAdjustForm({ ...adjustForm, notes: e.target.value })} placeholder="e.g. Amazon order #123" />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-primary btn-sm" onClick={() => handleAdjust('add')}>+ Add</button>
                <button className="btn btn-danger btn-sm" onClick={() => handleAdjust('remove')}>- Remove</button>
              </div>
            </div>

            {/* Log History */}
            {detail.log.length > 0 && (
              <div>
                <h4 style={{ marginBottom: '8px', fontSize: '14px' }}>Recent History</h4>
                <div className="table-container">
                  <table>
                    <thead>
                      <tr><th>Date</th><th>Change</th><th>After</th><th>Reason</th><th>By</th><th>Notes</th></tr>
                    </thead>
                    <tbody>
                      {detail.log.map(l => (
                        <tr key={l.id}>
                          <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDateTime(l.created_at)}</td>
                          <td style={{ fontWeight: '600', color: l.change_qty > 0 ? 'var(--success)' : 'var(--danger)' }}>
                            {l.change_qty > 0 ? '+' : ''}{l.change_qty}
                          </td>
                          <td>{l.stock_after}</td>
                          <td><span className="badge badge-default">{l.reason}</span></td>
                          <td style={{ fontSize: '12px' }}>{l.user_name}</td>
                          <td style={{ fontSize: '12px', color: 'var(--text-light)' }}>{l.notes || '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => openEdit(selected)}>Edit</button>
              <button className="btn btn-danger btn-sm" onClick={() => handleDelete(selected)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
